import { v4 as uuidv4 } from 'uuid'
import {
  GPIO_EVENTS,
  isGpioEventBindingSupported,
  normalizeGpioBindings,
  validateGpioBindings,
} from '../gpio/GpioBindingModel.js'
import {
  eventPropertyOptions,
  rangeForObject,
  rangeForProperty,
} from '../gpio/gpioEditorModel.js'
import { getEventVariables } from './commandContext.js'

export const GPIO_ENTITY_ACTION_TYPE = 'gpioEntity'

/** Entity properties a GPIO Entity action can set. */
export const GPIO_ACTION_PROPERTIES = Object.freeze(['state', 'brightness', 'rgb_color'])

const SOURCE_PROPERTIES = new Set(['val', 'state'])
const BINDING_PROPERTIES = new Set(['state', 'brightness'])
const COLOR_VARIABLES = ['r', 'g', 'b']

function numberOr(value, fallback) {
  return Number.isFinite(Number(value)) ? Math.trunc(Number(value)) : fallback
}

const isOn = value => value === true || value === 'true' || value === 'on'

function eventSourceForProperty(property) {
  if (property === 'rgb_color') return 'color'
  return property === 'brightness' ? 'val' : 'state'
}

function defaultRanges({ property, sourceProperty, node, entity }) {
  const input = sourceProperty === 'state'
    ? { min: 0, max: 1 }
    : rangeForObject(node)
  const output = rangeForProperty(entity, property)
  return { inMin: input.min, inMax: input.max, outMin: output.min, outMax: output.max }
}

/** `#rrggbb` (an alpha suffix is ignored) → [r, g, b], or null. */
export function parseRgbColor(value) {
  const match = /^#?([0-9a-f]{6})(?:[0-9a-f]{2})?$/i.exec(String(value ?? '').trim())
  if (!match) return null
  const hex = match[1]
  return [0, 2, 4].map(index => parseInt(hex.slice(index, index + 2), 16))
}

export function createGpioEntityAction(entity = null, node = null, event = '') {
  const colorEvent = COLOR_VARIABLES.every(variable => getEventVariables(node, event).includes(variable))
  const property = eventPropertyOptions(node?.obj, event)[0] ?? (colorEvent ? 'rgb_color' : 'state')
  const sourceProperty = eventSourceForProperty(property)
  const ranges = defaultRanges({ property, sourceProperty, node, entity })
  return {
    id: uuidv4(),
    type: GPIO_ENTITY_ACTION_TYPE,
    entity: entity?.id ?? '',
    property,
    valueSource: 'event',
    sourceProperty,
    ...ranges,
    fixedValue: property === 'state' ? true : property === 'rgb_color' ? '#ffffff' : ranges.outMin,
  }
}

/**
 * How a GPIO Entity action exports: as a firmware event binding on its source
 * object (`gpio_bindings.events`), or as an entity command in the event's
 * `action` list (`gpio/entity/<id> {…}`, docs/GPIO-LOGIC-FIRMWARE-SPEC.md §3).
 * A binding is used wherever the firmware supports it for the source object,
 * event and property; everything else (colour, toggle, other events, GPIO and
 * page sources, conditional actions) is a command. Without a known source
 * object the binding contract is assumed, as before commands existed.
 * @returns {'binding'|'command'}
 */
export function gpioEntityActionForm(action, { event = '', node = null, nested = false } = {}) {
  if (nested || !BINDING_PROPERTIES.has(action?.property)) return 'command'
  if ((action.valueSource ?? 'event') === 'toggle') return 'command'
  if (!node) return GPIO_EVENTS.includes(event) ? 'binding' : 'command'
  return isGpioEventBindingSupported(node.obj === 'button' ? 'btn' : node.obj, event, action.property) ? 'binding' : 'command'
}

/** Convert the Designer action representation to the firmware event binding. */
export function gpioEntityActionToBinding(action, { event = '', node = null, entity = null } = {}) {
  const property = action?.property
  if (!BINDING_PROPERTIES.has(property)) return null
  const entityId = typeof action.entity === 'string' ? action.entity.trim() : ''
  if (!entityId) return null

  const valueSource = action.valueSource ?? 'event'
  if (valueSource === 'toggle') return null
  if (valueSource === 'fixed') {
    const fixedValue = property === 'state'
      ? (isOn(action.fixedValue) ? 1 : 0)
      : numberOr(action.fixedValue, rangeForProperty(entity, property).min)
    return {
      event,
      source_property: 'state',
      entity: entityId,
      property,
      in_min: 0,
      in_max: 1,
      out_min: fixedValue,
      out_max: fixedValue,
    }
  }

  const sourceProperty = SOURCE_PROPERTIES.has(action.sourceProperty)
    ? action.sourceProperty
    : eventSourceForProperty(property)
  const ranges = defaultRanges({ property, sourceProperty, node, entity })
  return {
    event,
    source_property: sourceProperty,
    entity: entityId,
    property,
    in_min: numberOr(action.inMin, ranges.inMin),
    in_max: numberOr(action.inMax, ranges.inMax),
    out_min: numberOr(action.outMin, ranges.outMin),
    out_max: numberOr(action.outMax, ranges.outMax),
  }
}

// `{val}`, or `{val:in_min:in_max:out_min:out_max}` when the mapping is not the identity.
function valuePlaceholder(action) {
  const range = [action.inMin, action.inMax, action.outMin, action.outMax].map(value => numberOr(value, null))
  if (range.some(value => value === null) || (range[0] === range[2] && range[1] === range[3])) return '{val}'
  return `{val:${range.join(':')}}`
}

function commandValue(action) {
  const valueSource = action.valueSource ?? 'event'
  if (action.property === 'state') {
    if (valueSource === 'toggle') return '"toggle"'
    if (valueSource === 'fixed') return isOn(action.fixedValue) ? 'true' : 'false'
    return '{val}'
  }
  if (action.property === 'brightness') {
    if (valueSource === 'fixed') {
      const value = numberOr(action.fixedValue, null)
      return value === null ? null : String(value)
    }
    return valuePlaceholder(action)
  }
  if (action.property === 'rgb_color') {
    if (valueSource === 'fixed') {
      const rgb = parseRgbColor(action.fixedValue)
      return rgb ? `[${rgb.join(',')}]` : null
    }
    return `[${COLOR_VARIABLES.map(variable => `{${variable}}`).join(',')}]`
  }
  return null
}

/** Convert a GPIO Entity action to its entity command line, or null when incomplete. */
export function gpioEntityActionToCommand(action) {
  const entityId = typeof action?.entity === 'string' ? action.entity.trim() : ''
  if (!entityId || !GPIO_ACTION_PROPERTIES.includes(action.property)) return null
  if ((action.valueSource ?? 'event') === 'toggle' && action.property !== 'state') return null
  const value = commandValue(action)
  return value === null ? null : `gpio/entity/${entityId} {"${action.property}":${value}}`
}

/**
 * Firmware features an entity command relies on beyond what openhasp-next
 * runs today (docs/GPIO-LOGIC-FIRMWARE-SPEC.md §3–4), as short labels.
 */
export function gpioCommandFirmwareFeatures(command) {
  const text = String(command ?? '')
  return [
    text.includes('"toggle"') && 'state toggle',
    text.includes('"state":{val}') && 'state from {val}',
    /\{val:-?\d+:/.test(text) && 'mapped {val}',
    /\{[rgb]\}/.test(text) && 'colour placeholders',
  ].filter(Boolean)
}

/** Convert an existing firmware binding into the normal Designer action model. */
export function gpioBindingToEntityAction(binding, index = 0) {
  return {
    id: binding?.id ?? `gpio-event-${index + 1}`,
    type: GPIO_ENTITY_ACTION_TYPE,
    entity: binding?.entity ?? '',
    property: binding?.property ?? '',
    valueSource: 'event',
    sourceProperty: SOURCE_PROPERTIES.has(binding?.source_property)
      ? binding.source_property
      : eventSourceForProperty(binding?.property),
    ...(binding?.in_min === undefined ? {} : { inMin: binding.in_min }),
    ...(binding?.in_max === undefined ? {} : { inMax: binding.in_max }),
    ...(binding?.out_min === undefined ? {} : { outMin: binding.out_min }),
    ...(binding?.out_max === undefined ? {} : { outMax: binding.out_max }),
    fixedValue: binding?.property === 'state' ? true : binding?.out_min ?? 0,
  }
}

function bindingKey(binding) {
  return [binding?.event, binding?.source_property, binding?.entity, binding?.property,
    binding?.in_min, binding?.in_max, binding?.out_min, binding?.out_max].join('|')
}

/**
 * Merge legacy gpio_bindings.events into the existing ordered action handlers.
 * State bindings are intentionally not touched here.
 */
export function migrateGpioEventBindings(handlers, rawBindings) {
  const normalized = normalizeGpioBindings(rawBindings)
  const result = Array.isArray(handlers) ? handlers : []
  const existing = new Set()
  result.forEach(handler => (handler.actions ?? []).forEach(action => {
    if (action?.type !== GPIO_ENTITY_ACTION_TYPE) return
    const binding = gpioEntityActionToBinding(action, { event: handler.event })
    if (binding) existing.add(bindingKey(binding))
  }))

  normalized.value?.events?.forEach((binding, index) => {
    const action = gpioBindingToEntityAction(binding, index)
    const normalizedBinding = gpioEntityActionToBinding(action, { event: binding.event })
    if (!normalizedBinding || existing.has(bindingKey(normalizedBinding))) return
    let handler = result.find(item => item.event === binding.event)
    if (!handler) {
      handler = { id: uuidv4(), event: binding.event ?? '', actions: [] }
      result.push(handler)
    }
    handler.actions ??= []
    handler.actions.push(action)
    existing.add(bindingKey(normalizedBinding))
  })
  return result
}

/**
 * Value sources a GPIO Entity action can use for `property` on `event` of
 * `node`: an event value when the binding contract covers it or the event
 * carries the value (`{val}`, or `{r}{g}{b}` for a colour), a fixed value,
 * and toggling the state. Without a known source every source is offered.
 */
export function gpioActionValueSources(property, { event = '', node = null } = {}) {
  const sources = []
  if (!node || gpioEventValueAvailable(property, { event, node })) sources.push('event')
  sources.push('fixed')
  if (property === 'state') sources.push('toggle')
  return sources
}

function gpioEventValueAvailable(property, { event, node }) {
  const variables = getEventVariables(node, event)
  if (property === 'rgb_color') return COLOR_VARIABLES.every(variable => variables.includes(variable))
  return variables.includes('val') || gpioEntityActionForm({ property, valueSource: 'event' }, { event, node }) === 'binding'
}

function commandMetadataError(action, entity) {
  if (!entity) return ''
  const properties = entity.properties ?? {}
  const advertised = Object.hasOwn(properties, action.property) ||
    (Array.isArray(entity.capabilities) ? entity.capabilities : []).includes(action.property)
  const metadataComplete = Object.hasOwn(entity, 'properties') || Object.hasOwn(entity, 'capabilities')
  if (metadataComplete && !advertised) {
    return `Property '${action.property}' is not advertised by entity '${action.entity}'.`
  }
  if (properties[action.property]?.writable === false) {
    return `Property '${action.property}' on entity '${action.entity}' is read-only for an Event Action.`
  }
  return ''
}

function outOfRange(property, values) {
  return Number.isFinite(property?.min) && Number.isFinite(property?.max) &&
    values.some(value => value < property.min || value > property.max)
}

function validateGpioEntityCommand(action, { node, handler, entity }) {
  const metadataError = commandMetadataError(action, entity)
  if (metadataError) return metadataError
  const valueSource = action.valueSource ?? 'event'
  if (valueSource === 'toggle' && action.property !== 'state') return 'Only the state can be toggled'
  if (valueSource === 'event' && node && !gpioEventValueAvailable(action.property, { event: handler?.event, node })) {
    return action.property === 'rgb_color'
      ? 'This event provides no colour; choose a fixed colour'
      : 'This event provides no value; choose a fixed value'
  }
  if (!gpioEntityActionToCommand(action)) {
    return action.property === 'rgb_color' ? 'Select a colour' : 'Value is invalid for this property'
  }
  const property = entity?.properties?.[action.property]
  if (action.property === 'brightness') {
    const values = valueSource === 'fixed' ? [numberOr(action.fixedValue, 0)]
      : valueSource === 'event' ? [numberOr(action.outMin, 0), numberOr(action.outMax, 255)] : []
    if (outOfRange(property ?? { min: 0, max: 255 }, values)) {
      return `GPIO property '${action.property}' mapping is outside the advertised range ${property?.min ?? 0}–${property?.max ?? 255}.`
    }
  }
  return ''
}

export function validateGpioEntityAction(action, { node = null, handler = null, metadata = null, nested = false } = {}) {
  const entity = Array.isArray(metadata?.entities)
    ? metadata.entities.find(item => item?.id === action?.entity)
    : null
  const entityId = typeof action?.entity === 'string' ? action.entity.trim() : ''
  if (!entityId || !GPIO_ACTION_PROPERTIES.includes(action?.property)) {
    return 'Select a GPIO entity and a supported property'
  }
  if (gpioEntityActionForm(action, { event: handler?.event, node, nested }) === 'command') {
    return validateGpioEntityCommand(action, { node, handler, entity })
  }
  const binding = gpioEntityActionToBinding(action, {
    event: handler?.event,
    node,
    entity,
  })
  if (!binding) return 'Select a GPIO entity and a supported property'
  const result = validateGpioBindings({ events: [binding], state: [] }, {
    objectType: node?.obj,
    objectConfig: node,
    metadata,
  })
  if (result.errors[0]?.message) return result.errors[0].message
  const property = entity?.properties?.[binding.property]
  if (outOfRange(property, [binding.out_min, binding.out_max])) {
    return `GPIO property '${binding.property}' mapping is outside the advertised range ${property.min}–${property.max}.`
  }
  return ''
}
